import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useRecoilValue } from 'recoil'
import Card from 'shared/Cards/Card'
import TextField from 'shared/Forms/TextField'
import { InspectorCheckResult } from '../../../../inspection.types'
import useProject from 'screens/Projects/hooks/useProject'
import organizationsSelectors from 'screens/Admin/Organizations/organizations.selectors'
import projectTechnologiesSelectors from 'screens/Admin/ProjectTechnologies/projectTechnologies.selectors'
import timeUtils from 'utils/time.utils'
import Button from 'shared/Buttons/Button/Button'
import authAtom from 'screens/Auth/auth.atom'
import inspectionApi from '../../../../inspection.api'

interface IProps {
  checkResult: InspectorCheckResult
  setCheckResult: (next: InspectorCheckResult | ((prev: InspectorCheckResult) => InspectorCheckResult)) => void
}

const getStatus = (r: InspectorCheckResult): string => {
  if (r.grade !== null) return 'Проверено'
  if (r.inspectorId !== null) return 'Проверяется'
  return 'Не проверено'
}

const SubmissionInfoCard = (props: IProps) => {
  const { checkResult, setCheckResult } = props
  const navigate = useNavigate()
  const { project } = useProject(checkResult.projectId)
  const organizationsMap = useRecoilValue(organizationsSelectors.organiaztionsMap)
  const keyTechnologiesMap = useRecoilValue(projectTechnologiesSelectors.projectTechnologiesMap)
  const currentUser = useRecoilValue(authAtom).user
  const [assigning, setAssigning] = useState(false)

  const handleAssignSelf = () => {
    if (!currentUser?.id) return
    setAssigning(true)
    inspectionApi.assignInspector(checkResult.id, currentUser.id).then((resp) => {
      setAssigning(false)
      if (resp.status === 'success') {
        setCheckResult({ 
          ...checkResult, 
          inspectorId: currentUser.id, 
          inspector: { id: currentUser.id, info: currentUser.info } 
        })
      }
    })
  }

  const customerName = project?.organizationId ? organizationsMap[project.organizationId]?.name : null
  const mentorName = project?.mentor?.info?.name ?? null
  const tagsText =
    project?.keyTechnologies
      ?.map((t) => keyTechnologiesMap[t.technologyId]?.title)
      .filter(Boolean)
      .join(', ') ?? null

  return (
    <Card type='normal'>
      <div className='flex-lines'>
        <div style={{ marginBottom: '16px' }} className='flex-space-between'>
          <h2 className='text--caption-l text-700'>Информация</h2>
          {checkResult.teamId != null && (
            <span
              className='text--link text--link-underline'
              onClick={() => navigate(`/teams/${checkResult.teamId}`)}
              role='link'
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && navigate(`/teams/${checkResult.teamId}`)}
            >
              Посмотреть информацию о команде
            </span>
          )}
        </div>
        <div className='flex-lines'>
          <div className='grid-form-2'>
            <TextField header='Команда'>{checkResult.team?.name ?? '—'}</TextField>
            <TextField header='Название проекта'>{checkResult.project?.name ?? '—'}</TextField>
            <TextField header='Заказчик'>{customerName ?? '—'}</TextField>
            <TextField header='Куратор'>{mentorName ?? '—'}</TextField>
            <TextField header='Тип проекта'>{checkResult.project?.projectTypeId ?? '—'}</TextField>
            <TextField header='Этап'>{checkResult.checkpoint?.name ?? '—'}</TextField>
            <TextField header='Теги'>{tagsText ?? '—'}</TextField>
            <div className='flex-space-between'>
              <TextField style={{ flex: 5 }} header='Эксперт'>{checkResult.inspector?.info?.name ?? '—'}</TextField>
              {(!checkResult.inspector || currentUser?.isAdmin) && (
                <Button
                  style={{ flex: 1 }}
                  theme='grey-outline'
                  onClick={handleAssignSelf}
                  loading={assigning}
                  disabled={assigning}
                >
                  Назначить себя
                </Button>
              )}
            </div>
            <TextField header='Статус'>{getStatus(checkResult)}</TextField>
            <TextField header='Дата сдачи'>{timeUtils.formatDate(new Date(checkResult.createdAt), 'dd MMM yyyy')}</TextField>
            <TextField header='Оценка'>{checkResult.grade ?? '—'}</TextField>
          </div>
        </div>
      </div>
    </Card>
  )
}

export default SubmissionInfoCard
